import { jwtDecode } from "jwt-decode";
import { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import CircleAvatar from "../../utils/Register/circular_profile_img";
import { showToast } from "../../components/ToastNotification";
import HorizontalSelector from "../../utils/HorizontalSelector";
import { useNavigate } from "react-router-dom";
import { logoutUser } from "../../redux/user/userSlice";
import { persistor } from "../../redux/store";

const Profile = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate(); // si estás usando React Router

  const handleLogout = async () => {
    dispatch(logoutUser()); // Limpia el estado Redux
    await persistor.purge(); // Limpia el storage persistente
    navigate("/signin"); // Redirige al login
  };
  const token = useSelector((state) => state.user.token);
  const [userData, setUserData] = useState({
    first_name: "",
    last_name: "",
    email: "",
    user_type: "",
    profile_picture_url: "",
  });
  const [editMode, setEditMode] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [userId, setUserId] = useState("");

  const playersData = [
    {
      title: "Jugadores",
      cards: [
        {
          image: "/images/jugador1.jpg",
          text1: "Mis torneos",
          text2: "Edad: 25",
          text3: "Tigres FC",
          link: "/my-tournaments",
        },
        {
          image: "/images/jugador2.jpg",
          text1: "equipos",
          text2: "Edad: 29",
          text3: "Águilas",
          link: "/my-teams",
        },
        // más jugadores...
      ],
    },
  ];

  useEffect(() => {
    if (token) {
      const decodedToken = jwtDecode(token);
      setUserId(decodedToken.id);
    }
  }, [token]);

  // Obtener los datos del usuario
  useEffect(() => {
    if (!userId) return;

    const fetchUserData = async () => {
      try {
        const response = await fetch(
          `${process.env.REACT_APP_API_URL}/api/users/${userId}`,
          {
            method: "GET",
            headers: {
              Authorization: `Bearer ${token}`,
            },
          },
        );

        if (!response.ok) {
          throw new Error("Error al cargar los datos del usuario.");
        }

        const data = await response.json();
        setUserData(data);
      } catch (error) {
        setError("Error al cargar los datos del usuario.");
        console.error("Error:", error);
      }
    };

    fetchUserData();
  }, [userId, token]);

  // Manejar cambios en los campos del formulario
  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setUserData({ ...userData, [name]: value });
  };

  // Manejar la subida de la foto de perfil
  const handleFileChange = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    const formData = new FormData();
    formData.append("profile_image", file);

    try {
      const response = await fetch(
        `${process.env.REACT_APP_API_URL}/api/users/${userId}/upload-profile-picture`,
        {
          method: "POST",
          headers: {
            Authorization: `Bearer ${token}`,
          },
          body: formData,
        },
      );

      if (!response.ok) {
        throw new Error("Error al subir la foto de perfil.");
      }

      const data = await response.json();
      setUserData({ ...userData, profile_picture_url: data.url });
      showToast.success("Foto de perfil actualizada correctamente.");
    } catch (error) {
      setError("Error al subir la foto de perfil.");
      showToast.error("Error al subir la foto de perfil.");
    }
  };

  // Guardar los cambios en el perfil
  const handleSave = async () => {
    try {
      const response = await fetch(
        `${process.env.REACT_APP_API_URL}/api/user`,
        {
          method: "PUT",
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
          body: JSON.stringify(userData),
        },
      );

      if (!response.ok) {
        throw new Error("Error al actualizar el perfil.");
      }

      const data = await response.json();
      setUserData(data);
      setEditMode(false);
      setSuccess("Perfil actualizado correctamente.");
    } catch (error) {
      setError("Error al actualizar el perfil.");
      console.error("Error:", error);
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 flex flex-col items-center px-4 font-sans">
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Inter:wght@400;500;600;700;800&display=swap');
        body { font-family: 'Inter', sans-serif; }
      `}</style>

      {/* Header con fondo degradado */}
      <div className="w-full max-w-[380px] relative bg-gradient-to-r from-emerald-500 to-emerald-600 h-40 rounded-b-3xl shadow-md">
        <h1 className="text-white text-xl font-semibold absolute top-5 left-1/2 transform -translate-x-1/2">
          Perfil de Usuario
        </h1>

        {/* Avatar flotante */}
        <div className="absolute bottom-[-40px] left-1/2 transform -translate-x-1/2">
          <CircleAvatar
            size={150}
            onImageSelect={handleFileChange}
            imageUrl={userData.profile_picture_url}
          />
        </div>
      </div>

      {/* Contenido principal */}
      <div className="w-full max-w-[380px] mt-16 bg-white rounded-2xl shadow-md p-6 space-y-6 text-gray-700">
        {/* Mensajes de error / éxito */}
        {error && <p className="text-red-500 text-sm text-center">{error}</p>}
        {success && (
          <p className="text-green-500 text-sm text-center">{success}</p>
        )}

        {/* Datos del usuario */}
        <div className="space-y-4">
          {[
            { label: "Nombre", value: userData.first_name, name: "first_name" },
            { label: "Apellido", value: userData.last_name, name: "last_name" },
            { label: "Correo", value: userData.email, editable: false },
            {
              label: "Tipo de usuario",
              value: userData.user_type,
              name: "user_type",
            },
          ].map(({ label, value, name, editable = true }) => (
            <div key={label}>
              <label className="text-xs text-gray-500">{label}</label>
              {editMode && editable ? (
                <input
                  name={name}
                  type="text"
                  value={value}
                  onChange={handleInputChange}
                  className="w-full mt-1 px-3 py-2 text-sm border border-gray-300 rounded-2xl focus:outline-none focus:ring-2 focus:ring-emerald-400 transition"
                />
              ) : (
                <p className="mt-1 px-3 py-2 text-sm bg-gray-100 rounded-2xl">
                  {value}
                </p>
              )}
            </div>
          ))}
        </div>

        {/* Botones de acción */}
        <div className="flex flex-wrap justify-center gap-3 mt-4">
          {editMode ? (
            <>
              <button
                onClick={handleSave}
                className="bg-emerald-500 hover:bg-emerald-600 text-white text-sm px-5 py-2 rounded-full shadow-md transition"
              >
                Guardar
              </button>
              <button
                onClick={() => setEditMode(false)}
                className="bg-gray-300 hover:bg-gray-400 text-gray-800 text-sm px-5 py-2 rounded-full shadow transition"
              >
                Cancelar
              </button>
            </>
          ) : (
            <button
              onClick={() => setEditMode(true)}
              className="bg-emerald-500 hover:bg-emerald-600 text-white text-sm px-6 py-2 rounded-full shadow-md transition"
            >
              Editar Perfil
            </button>
          )}
          <button
            onClick={handleLogout}
            className="bg-red-500 hover:bg-red-600 text-white text-sm px-6 py-2 rounded-full shadow-md transition"
          >
            Cerrar Sesión
          </button>
        </div>

        {/* HorizontalSelector */}
        <HorizontalSelector items={playersData} />
      </div>
    </div>
  );
};

export default Profile;
